import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';

interface ActionWrapSignature {
  Element: HTMLAnchorElement | HTMLButtonElement;
  Args: {
    string: string;
    href?: string;
    action?(event: MouseEvent): void;
  };
  Blocks: {
    default: [wrappedText: string];
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    ActionWrap: typeof ActionWrap;
  }
}

/**
 *  <ActionWrap></ActionWrap> component
 *
 *  Used to parse out strings with underscore-wrapped segments
 *  (e.g.: from our translation files) and either wrap them with an anchor tag,
 *  button element or custom passed in block
 *
 *  Note: This component only works with a single link string. Multiple linked
 *  strings is not supported.
 *
 *  @public
 *  @example
 *  <ActionWrap
 *    @string="This is a string _with a link_."
 *    @href="#"
 *  />
 *
 *  Yields:
 *  This is a string <a href='#' class='whatever'>with a link</a>.
 *
 *  or
 *
 *  <ActionWrap
 *    @string="This is a string _with a link_."
 *    @action={{fn this.someAction}}
 *  />
 *
 *  Yields:
 *  This is a string <button onclick=@someAction>with a link</button>.
 *
 *  or
 *
 *  <ActionWrap
 *    @string="This is a string _with a link_." as |text|
 *  >
 *    <button type="button">{{text}}</button>
 *  </ActionWrap>
 *
 *
 */
export default class ActionWrap extends Component<ActionWrapSignature> {
  get before(): string {
    return String(this.args.string).split('_')?.[0] ?? '';
  }

  get after(): string {
    return String(this.args.string).split('_')?.[2] ?? '';
  }

  get wrappedText(): string {
    let string = String(this.args.string);

    /**
     * Match occurrences of anything surrounded by two underscores.
     * e.g.: Hello, _world_. => ['_world_', 'world']
     **/
    let regex = /_([^_]+)_/;

    return string.match(regex)?.[1] || '';
  }

  @action
  callActionPassed(evt: MouseEvent): void {
    this.args.action?.(evt);
  }

  <template>
    {{#if this.wrappedText}}
      {{this.before~}}
      {{#if (has-block)}}
        {{~yield this.wrappedText~}}
      {{~else~}}
        {{~#if @href~}}
          <a
            href={{@href}}
            target='_blank'
            rel='noreferrer noopener'
            {{on 'click' this.callActionPassed}}
            ...attributes
            data-test-action-href
          >{{this.wrappedText}}</a>
        {{~else if @action~}}
          <button
            {{on 'click' @action}}
            type='button'
            ...attributes
            data-test-action-button
          >{{this.wrappedText}}</button>
        {{~else~}}
          {{this.wrappedText}}
        {{~/if~}}
      {{~/if~}}
      {{~this.after}}
    {{else}}
      {{@string}}
    {{/if}}
  </template>
}
